import { apiFetch } from '../api/client';
import { downloadFileUrl } from '../fileBrowser/fileBrowserApi';
import { showErrorToast, showSuccessToast, showToast } from '../notifications/toast';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import type { DocumentEditorKind } from './documentFileTypes';
import { documentEditorHref } from './documentEditorUrls';

export async function downloadDocumentFile(mountId: string, relativePath: string): Promise<void> {
  if (!mountId || !relativePath) return;

  const url = downloadFileUrl(mountId, relativePath);
  const fileName = displayNameFromPath(relativePath) || 'download';

  try {
    const response = await apiFetch(url);
    if (!response.ok) {
      showErrorToast('Download failed.');
      return;
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    showToast('Download started.', 'success');
  } catch {
    showErrorToast('Download failed.');
  }
}

export async function shareDocumentLink(
  kind: DocumentEditorKind,
  mode: 'edit' | 'preview',
  mountId: string,
  relativePath: string
): Promise<void> {
  if (!mountId || !relativePath) return;

  const url = new URL(documentEditorHref(kind, mode, mountId, relativePath), window.location.origin).href;
  const title = displayNameFromPath(relativePath);

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url, title });
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
  }

  if (!navigator.clipboard?.writeText) {
    showErrorToast('Sharing is not supported in this browser.');
    return;
  }

  try {
    await navigator.clipboard.writeText(url);
    showSuccessToast('Link copied to clipboard.');
  } catch {
    showErrorToast('Could not copy the link.');
  }
}

export async function shareDocumentPreviewLink(
  kind: DocumentEditorKind,
  mountId: string,
  relativePath: string
): Promise<void> {
  return shareDocumentLink(kind, 'preview', mountId, relativePath);
}
